import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Modal,
  Notice,
  SearchBox,
  Select,
  Spinner,
  Table,
  TableWrap,
  Toolbar,
  useToast,
} from 'iboon-ui';

import { api, type Category, type StockItem } from '../api';

/**
 * The counted things.
 *
 * Five numbers per row, and none of them is stored. Every one is worked out
 * from the movement ledger when the page is loaded, which is why they cannot
 * disagree with each other -- there is no column for any of them to drift
 * away from.
 */
export function Stock() {
  const [items, setItems] = useState<StockItem[] | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async (q: string) => {
    const result = await api.get<{ items: StockItem[] }>(
      `/api/stock${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ''}`,
    );
    setItems(result.items);
  }, []);

  useEffect(() => {
    api.get<{ categories: Category[] }>('/api/categories').then((r) => setCategories(r.categories));
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setItems(null);
      load(query).catch((caught: unknown) =>
        setError(caught instanceof Error ? caught.message : 'Could not load stock.'),
      );
    }, 200);
    return () => clearTimeout(timer);
  }, [query, load]);

  if (error) {
    return (
      <Notice tone="danger" title="Could not load stock">
        {error}
      </Notice>
    );
  }

  return (
    <>
      <Toolbar>
        <SearchBox value={query} onValueChange={setQuery} placeholder="Cable, adapter, mouse…" />
        <Button variant="primary" onClick={() => setAdding(true)}>
          Add a kind of thing
        </Button>
      </Toolbar>

      {!items ? (
        <Spinner />
      ) : items.length === 0 ? (
        <EmptyState
          title={query ? 'Nothing matches' : 'No stock yet'}
          description={
            query
              ? 'No kind of thing matches that.'
              : 'Cables, adapters, spare mice — anything you count rather than number.'
          }
        />
      ) : (
        <Card padded={false}>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>What</th>
                  <th>Category</th>
                  <th className="num">Have</th>
                  <th className="num">Out</th>
                  <th className="num">Free</th>
                  <th className="num">Broken</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td className="tcell-name">
                      <Link to={`/stock/${item.id}`}>{item.name}</Link>
                      <div className="sub">
                        {[item.make, item.model].filter(Boolean).join(' ') || '—'}
                      </div>
                    </td>
                    <td>{item.categoryName}</td>
                    <td className="num">{item.total}</td>
                    <td className="num">{item.assigned}</td>
                    <td className="num">
                      <b>{item.available}</b>
                    </td>
                    <td className="num">
                      {item.defective > 0 ? (
                        <span className="muted">{item.defective}</span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td>
                      {/* Only when it means something. A "low" badge on every
                          row with no reorder level set is a badge nobody
                          reads. */}
                      {item.reorderLevel > 0 && item.available <= item.reorderLevel && (
                        <Badge tone="danger">running low</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        </Card>
      )}

      {adding && (
        <AddStock
          categories={categories.filter((c) => c.tracking === 'stock')}
          onClose={() => setAdding(false)}
          onAdded={() => {
            setAdding(false);
            void load(query);
          }}
        />
      )}
    </>
  );
}

function AddStock({
  categories,
  onClose,
  onAdded,
}: {
  categories: Category[];
  onClose: () => void;
  onAdded: () => void;
}) {
  const [form, setForm] = useState({
    categoryId: categories[0]?.id ?? '',
    name: '',
    make: '',
    model: '',
    reorderLevel: '0',
  });
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const set = (key: keyof typeof form) => (event: { target: { value: string } }) =>
    setForm((was) => ({ ...was, [key]: event.target.value }));

  async function save() {
    setBusy(true);
    try {
      await api.post('/api/stock', { ...form, reorderLevel: Number(form.reorderLevel) || 0 });
      toast.show(`${form.name} added. Record what you have with a “received” movement.`);
      onAdded();
    } catch (caught) {
      toast.show(caught instanceof Error ? caught.message : 'Could not add it.', { tone: 'danger' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open size="sm" title="Add a kind of thing" onClose={onClose}>
      {categories.length === 0 ? (
        <Notice tone="accent" title="No stock categories yet">
          Stock needs a category marked as counted rather than tracked one by one — Cables,
          Adapters. Add one first.
        </Notice>
      ) : (
        <>
          <Field label="Category" required>
            <Select value={form.categoryId} onChange={set('categoryId')}>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="What is it" required hint="The kind, not one of them: “USB-C cable, 1m”.">
            <Input value={form.name} onChange={set('name')} />
          </Field>
          <Field label="Make">
            <Input value={form.make} onChange={set('make')} />
          </Field>
          <Field label="Model">
            <Input value={form.model} onChange={set('model')} />
          </Field>
          <Field
            label="Tell me when there are fewer than"
            hint="Leave at zero to never be told."
          >
            <Input type="number" min={0} value={form.reorderLevel} onChange={set('reorderLevel')} />
          </Field>

          <div className="row-actions" style={{ marginTop: 14, justifyContent: 'flex-start' }}>
            <Button variant="primary" disabled={busy || !form.name} onClick={() => void save()}>
              {busy ? 'Adding…' : 'Add it'}
            </Button>
            <Button variant="ghost" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}
